import React, { useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';

// Import the intro video
import introVideo from '../../img/CSG-Anim2.mp4';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const VideoContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: #000;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${({ $fadeOut }) => $fadeOut ? fadeOut : fadeIn} 0.5s ease forwards;
`;

const Video = styled.video`
  max-width: 100%;
  max-height: 100%;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const SkipHint = styled.div`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  font-size: 14px;
  animation: ${fadeIn} 1s ease 1s forwards;
  opacity: 0;
  cursor: pointer;
  user-select: none;

  &:hover {
    text-decoration: underline;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  font-size: 16px;
`;

/**
 * IntroVideo component - plays the ODIN1 intro video
 *
 * Features:
 * - Fullscreen video overlay
 * - Auto-plays when mounted
 * - Click/keypress to skip
 * - Graceful error handling
 * - Fade out transition on complete
 */
const IntroVideo = ({ onComplete }) => {
  const videoRef = useRef(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isFadingOut, setIsFadingOut] = React.useState(false);

  // Handle video end or skip
  const handleComplete = useCallback(() => {
    if (isFadingOut) return; // Prevent multiple calls

    setIsFadingOut(true);
    // Wait for fade out animation before calling onComplete
    setTimeout(() => {
      onComplete?.();
    }, 500);
  }, [isFadingOut, onComplete]);

  // Handle skip (click or keypress)
  const handleSkip = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    handleComplete();
  }, [handleComplete]);

  // Handle video loaded
  const handleCanPlay = useCallback(() => {
    setIsLoading(false);
    if (videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.warn('Video autoplay failed:', error);
        // If autoplay fails, skip the video
        handleComplete();
      });
    }
  }, [handleComplete]);

  // Handle video error
  const handleError = useCallback((error) => {
    console.error('Video error:', error);
    // Skip on error
    handleComplete();
  }, [handleComplete]);

  // Set up keyboard listener for skip
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Skip on any key press
      handleSkip();
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleSkip]);

  return (
    <VideoContainer $fadeOut={isFadingOut} onClick={handleSkip}>
      {isLoading && (
        <LoadingOverlay>Loading...</LoadingOverlay>
      )}
      <Video
        ref={videoRef}
        src={introVideo}
        muted
        playsInline
        preload="auto"
        onCanPlay={handleCanPlay}
        onEnded={handleComplete}
        onError={handleError}
      />
      <SkipHint>Click or press any key to skip</SkipHint>
    </VideoContainer>
  );
};

IntroVideo.propTypes = {
  onComplete: PropTypes.func.isRequired,
};

export default IntroVideo;
